import { Link, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { lazy, Suspense, type ReactNode } from "react";
import { AppShell } from "./components/AppShell.js";
import { ErrorState, LoadingState } from "./components/Page.js";
import { useSession } from "./session.js";
import { LoginPage } from "./pages/LoginPage.js";
import { MicrosoftSignInCompletePage } from "./components/MicrosoftSignIn.js";
import { PasswordRecoveryPage } from "./pages/PasswordRecoveryPage.js";
import { ReturnAfterLogin } from "./components/ReturnAfterLogin.js";
import { previousReturnTenant } from "./lib/auth-return-target.js";

const MailInboxPage = lazy(() =>
  import("./pages/MailInboxPage.js").then((module) => ({ default: module.MailInboxPage })),
);
const MailSettingsPage = lazy(() =>
  import("./pages/MailSettingsPage.js").then((module) => ({ default: module.MailSettingsPage })),
);
const ProfilePage = lazy(() =>
  import("./pages/ProfilePage.js").then((module) => ({ default: module.ProfilePage })),
);

const AdministrationPage = lazy(() =>
  import("./pages/AdministrationPage.js").then((module) => ({
    default: module.AdministrationPage,
  })),
);
const CrmAudiencesPage = lazy(() =>
  import("./pages/CrmAudiencesPage.js").then((module) => ({ default: module.CrmAudiencesPage })),
);
const CrmPage = lazy(() =>
  import("./pages/CrmPage.js").then((module) => ({ default: module.CrmPage })),
);
const DashboardPage = lazy(() =>
  import("./pages/DashboardPage.js").then((module) => ({ default: module.DashboardPage })),
);
const DocumentationPage = lazy(() =>
  import("./pages/DocumentationPage.js").then((module) => ({
    default: module.DocumentationPage,
  })),
);
const BemPage = lazy(() =>
  import("./pages/BemPage.js").then((module) => ({ default: module.BemPage })),
);
const KnowledgePage = lazy(() =>
  import("./pages/KnowledgePage.js").then((module) => ({ default: module.KnowledgePage })),
);
const FinancePage = lazy(() =>
  import("./pages/FinancePage.js").then((module) => ({ default: module.FinancePage })),
);
const FileExplorerPage = lazy(() =>
  import("./pages/FileExplorerPage.js").then((module) => ({ default: module.FileExplorerPage })),
);
const HelpPage = lazy(() =>
  import("./pages/HelpPage.js").then((module) => ({ default: module.HelpPage })),
);
const LearningPage = lazy(() =>
  import("./pages/LearningPage.js").then((module) => ({ default: module.LearningPage })),
);
const LearnerLoginPage = lazy(() =>
  import("./pages/LearnerLoginPage.js").then((module) => ({ default: module.LearnerLoginPage })),
);
const LearnerPortalPage = lazy(() =>
  import("./pages/LearnerPortalPage.js").then((module) => ({ default: module.LearnerPortalPage })),
);
const MarketingPage = lazy(() =>
  import("./pages/MarketingPage.js").then((module) => ({ default: module.MarketingPage })),
);
const NotFoundPage = lazy(() =>
  import("./pages/NotFoundPage.js").then((module) => ({ default: module.NotFoundPage })),
);
const ProjectsPage = lazy(() =>
  import("./pages/ProjectsPage.js").then((module) => ({ default: module.ProjectsPage })),
);
const ProjectWorkspacePage = lazy(() =>
  import("./pages/ProjectWorkspacePage.js").then((module) => ({
    default: module.ProjectWorkspacePage,
  })),
);
const PublicSurveyPage = lazy(() =>
  import("./pages/PublicSurveyPage.js").then((module) => ({ default: module.PublicSurveyPage })),
);
const ReportsPage = lazy(() =>
  import("./pages/ReportsPage.js").then((module) => ({ default: module.ReportsPage })),
);
const CustomerSurveyDashboardPage = lazy(() =>
  import("./pages/CustomerSurveyDashboardPage.js").then((module) => ({
    default: module.CustomerSurveyDashboardPage,
  })),
);
const SurveysPage = lazy(() =>
  import("./pages/SurveysPage.js").then((module) => ({ default: module.SurveysPage })),
);

function ProtectedLayout() {
  const session = useSession();
  const location = useLocation();
  if (session.isLoading) {
    return (
      <main className="boot-screen">
        <img src="/brand/etain-logo.svg" alt="ETAIN" />
        <LoadingState cards={1} />
      </main>
    );
  }
  if (session.isError && !session.data) {
    return (
      <main className="boot-screen">
        <ErrorState error={session.error} retry={() => void session.refetch()} />
      </main>
    );
  }
  const targetTenant = new URLSearchParams(location.search).get("tenantId");
  if (!session.data)
    return (
      <Navigate
        to="/anmelden"
        replace
        state={{ from: location, tenantId: targetTenant ?? previousReturnTenant() }}
      />
    );
  if (targetTenant && targetTenant !== session.data.activeTenant.id)
    return (
      <ReturnAfterLogin
        key={`${session.data.user.id}:${location.pathname}:${location.search}:${location.hash}`}
        target={`${location.pathname}${location.search}${location.hash}`}
        tenantId={targetTenant}
      />
    );
  if (session.data.user.role === "learner") {
    if (location.pathname !== "/einstellungen/profil") return <Navigate to="/lernportal" replace />;
    return (
      <main className="profile-portal">
        <Link to="/lernportal">Zurück zum Lernportal</Link>
        <Outlet />
      </main>
    );
  }
  return <AppShell />;
}

function FeaturePage({ feature, children }: { feature: string; children: ReactNode }) {
  const session = useSession();
  if (session.data?.features[feature] !== true) {
    return <Navigate to="/" replace state={{ blockedFeature: feature }} />;
  }
  return children;
}

function CrmIndexRedirect() {
  const location = useLocation();
  const session = useSession();
  const target = session.data?.features.crmSalesWorkspace === true ? "vertrieb" : "dashboard";
  return <Navigate to={`/crm/${target}${location.search}`} replace />;
}

export function App() {
  return (
    <Suspense fallback={<LoadingState cards={3} />}>
      <Routes>
        <Route path="/auswertungen/:id" element={<CustomerSurveyDashboardPage />} />
        <Route path="/anmelden" element={<LoginPage />} />
        <Route path="/anmelden/microsoft" element={<MicrosoftSignInCompletePage />} />
        <Route
          path="/passwort-vergessen"
          element={<PasswordRecoveryPage key="request" mode="request" />}
        />
        <Route
          path="/passwort-zuruecksetzen"
          element={<PasswordRecoveryPage key="password_reset" mode="password_reset" />}
        />
        <Route
          path="/einladung-annehmen"
          element={<PasswordRecoveryPage key="invitation" mode="invitation" />}
        />
        <Route path="/lernportal/anmelden" element={<LearnerLoginPage />} />
        <Route path="/lernportal" element={<LearnerPortalPage />} />
        <Route path="/teilnehmen/:publicId" element={<PublicSurveyPage />} />
        <Route element={<ProtectedLayout />}>
          <Route index element={<DashboardPage />} />
          <Route path="einstellungen/profil" element={<ProfilePage />} />
          <Route path="dateiexplorer" element={<FileExplorerPage />} />
          <Route path="crm" element={<CrmIndexRedirect />} />
          <Route
            path="crm/e-mail"
            element={
              <FeaturePage feature="crm">
                <MailInboxPage />
              </FeaturePage>
            }
          />
          <Route
            path="einstellungen/e-mail"
            element={
              <FeaturePage feature="crm">
                <MailSettingsPage />
              </FeaturePage>
            }
          />
          <Route
            path="crm/vertrieb/:kind/:salesId"
            element={
              <FeaturePage feature="crm">
                <CrmPage />
              </FeaturePage>
            }
          />
          <Route
            path="crm/kunden/:customerId"
            element={
              <FeaturePage feature="crm">
                <CrmPage />
              </FeaturePage>
            }
          />
          <Route
            path="crm/kontakte/zielgruppen"
            element={
              <FeaturePage feature="crm">
                <CrmAudiencesPage />
              </FeaturePage>
            }
          />
          <Route
            path="crm/:section"
            element={
              <FeaturePage feature="crm">
                <CrmPage />
              </FeaturePage>
            }
          />
          <Route
            path="crm/:section/:recordId"
            element={
              <FeaturePage feature="crm">
                <CrmPage />
              </FeaturePage>
            }
          />
          <Route
            path="crm/organisationen/:organizationId"
            element={
              <FeaturePage feature="crm">
                <CrmPage />
              </FeaturePage>
            }
          />
          <Route
            path="projekte/:projectId"
            element={
              <FeaturePage feature="projects">
                <ProjectWorkspacePage />
              </FeaturePage>
            }
          />
          <Route
            path="projekte"
            element={
              <FeaturePage feature="projects">
                <ProjectsPage />
              </FeaturePage>
            }
          />
          <Route
            path="finanzen"
            element={
              <FeaturePage feature="finance">
                <FinancePage />
              </FeaturePage>
            }
          />
          <Route
            path="marketing"
            element={
              <FeaturePage feature="marketing">
                <MarketingPage />
              </FeaturePage>
            }
          />
          <Route
            path="lernen"
            element={
              <FeaturePage feature="learning">
                <LearningPage />
              </FeaturePage>
            }
          />
          <Route
            path="befragungen/:sourceId/dashboards/:dashboardId"
            element={
              <FeaturePage feature="surveys">
                <SurveysPage />
              </FeaturePage>
            }
          />
          <Route
            path="befragungen/:sourceId/:section"
            element={
              <FeaturePage feature="surveys">
                <SurveysPage />
              </FeaturePage>
            }
          />
          <Route
            path="befragungen/:sourceId"
            element={
              <FeaturePage feature="surveys">
                <SurveysPage />
              </FeaturePage>
            }
          />
          <Route
            path="befragungen"
            element={
              <FeaturePage feature="surveys">
                <SurveysPage />
              </FeaturePage>
            }
          />
          <Route
            path="bem/:caseId?"
            element={
              <FeaturePage feature="bem">
                <BemPage />
              </FeaturePage>
            }
          />
          <Route
            path="wissen"
            element={
              <FeaturePage feature="knowledge">
                <KnowledgePage />
              </FeaturePage>
            }
          />
          <Route
            path="wissen/:pageId"
            element={
              <FeaturePage feature="knowledge">
                <KnowledgePage />
              </FeaturePage>
            }
          />
          <Route
            path="berichte"
            element={
              <FeaturePage feature="reports">
                <ReportsPage />
              </FeaturePage>
            }
          />
          <Route
            path="administration/:section?"
            element={
              <FeaturePage feature="admin">
                <AdministrationPage />
              </FeaturePage>
            }
          />
          <Route
            path="dokumentation"
            element={
              <FeaturePage feature="documentation">
                <DocumentationPage />
              </FeaturePage>
            }
          />
          <Route path="hilfe" element={<HelpPage />} />
          {/*
            Catch-all innerhalb des geschützten Layouts: ProtectedLayout prüft die Sitzung
            zuerst, daher greift diese Route sowohl für angemeldete Nutzer (404-Seite mit
            AppShell-Rahmen) als auch für nicht angemeldete Nutzer (die schon in
            ProtectedLayout zu /anmelden umgeleitet werden, bevor diese Route zum Zug kommt).
            Eine zusätzliche Catch-all-Route außerhalb dieses Zweigs würde react-router nie
            erreichen: beide Routen hätten dieselbe Wildcard-Wertung, und bei Gleichstand
            gewinnt laut computeScore/compareIndexes in node_modules/react-router immer die
            zuerst deklarierte Route – hier also diese. Eine zweite, äußere Route wäre daher
            toter Code.
          */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
